
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import SettingsTitleBar from '@components/settings/SettingsTitleBar.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppEditableHeader from '@components/new-styleguide/AppEditableHeader.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import SettingsFooter from '@components/settings/SettingsFooter.vue';
import SettingsDelete from '@components/settings/SettingsDelete.vue';
import SettingsPanel from '@components/settings/SettingsPanel.vue';
import AppListItem, { ListItem } from '@components/new-styleguide/AppListItem.vue';
import { ruleV2Icons } from '@util/settingIcons'
import { LoadState } from '@util/types';
import { ZoneMutation } from '@/generated-clients/myTrips';
import { ZoneWithRules } from '@/models/SettingsModels';
import deepCopy from '@/utils/copy';
import ZoneSelect from '@components/settings/zone/ZoneSelect.vue';
import ZoneNewMap from '@components/settings/zone/ZoneMapbox.vue';
import { channelSubtitle } from '@util/channelSubtitle';
import goBackWithFallback from '@util/goBackWithFallback';
import { RouterFrom } from '@/router/Routes';

const SettingsModule = namespace('Settings');
const RouterModule = namespace('Router');

@Component({
  name: 'ZoneEdit',
  components: {
    ZoneNewMap,
    AppPage,
    AppContent,
    AppEditableHeader,
    SettingsTitleBar,
    SettingsFooter,
    SettingsDelete,
    SettingsPanel,
    AppListItem,
    InfoBoxWarning,
    ZoneSelect
  }
})
export default class ZoneEdit extends Vue {
  @SettingsModule.Getter('getZones')
  private zones: ZoneWithRules[]

  @SettingsModule.Action('fetchZones')
  private fetchZones;

  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Action('fetchTags')
  private fetchTags;

  @SettingsModule.Action('fetchRulesV2')
  private fetchRules;

  @SettingsModule.Action('updateZone')
  private updateZone;

  @SettingsModule.Action('deleteZone')
  private deleteZone;

  @SettingsModule.Getter('getZonesStatus')
  private zonesStatus: LoadState;

  @RouterModule.State('from')
  private from: RouterFrom;

  private input: ZoneMutation = {
    name: '',
    latitude: null,
    longitude: null,
    radius: null,
    shortParkingZoneNotification: true,
  };

  private isLoading = true;
  private disabled = true;
  private currentMovement = false;
  private errorMessage = '';

  async created() {
    const fetchNonZonesPromise = Promise.all[this.fetchRules(), this.fetchPilots(), this.fetchTags()];

    await this.fetchZones();
    this.input = deepCopy(this.zone);
    this.isLoading = false;

    try {
      await fetchNonZonesPromise;
    } finally {
      this.disabled = false;
    }
  }

  get deleteText() {
    return 'Sind Sie sicher, dass Sie die Zone löschen möchten? ' + (this.isSynchronizedWithGisFavorite) ? 'Diese Zone wurde automatisch mit einem Favoriten aus dem ÖAMTC Routenplaner zusammengeführt. Bitte beachten Sie, dass dieser auch dort gelöscht wird.' : '';
  }

  get updateText() {
    return 'Diese Zone wurde automatisch mit einem Favoriten aus dem ÖAMTC Routenplaner zusammengeführt. Bitte beachten Sie, dass dieser auch dort geändert wird.';
  }

  get isSynchronizedWithGisFavorite() {
    return this.zone.synchronizedWithGisFavorite === true;
  }

  get rulesHeadline() {
    return 'Diese Zone ist Teil folgender Regel' + (this.associatedRuleItems.length > 1 ? 'n' : '');
  }

  get titleValue() {
    return this.isLoading ? '' : this.input.name;
  }

  get placeholder() {
    return this.isLoading ? 'Wird geladen...' : 'Name hier eingeben';
  }

  get backButton() {
    return { text: 'Zurück zu Zielen', location: this.backLocation };
  }

  get backLocation() {
    return { name: 'zonesOverview' };
  }

  get zone() {
    return this.zones.find(p => p.id === this.id);
  }

  get associatedRuleItems(): ListItem[] {
    return (this.zone)
        ? this.zone.associatedRules
            .map(r => ({ id: r.id, title: r.name, subtitle: channelSubtitle(r), ...ruleV2Icons(r), location: { name: 'ruleEdit', params: { id: r.id.toString() } }, hoverText: 'Regel bearbeiten' }))
        : [];
  }

  get title() {
    return this.zone ? this.zone.name : '';
  }

  get deleteModalHeader() {
    return `Zone ${this.title} löschen`;
  }

  get id() {
    return parseInt(this.$route.params.id);
  }

  get nameError() {
    return !this.input.name;
  }

  get errorsExist() {
    return this.nameError;
  }

  get errorLoading() {
    return this.zonesStatus === LoadState.Error;
  }

  get showAssociatedRules() {
    return !this.isLoading && this.associatedRuleItems.length > 0;
  }

  private hasNameOrLocationChanged() {
    return this.zone.name !== this.input.name
      || this.zone.latitude !== this.input.latitude
      || this.zone.longitude !== this.input.longitude;
  }

  private async submit() {
    if (this.isSynchronizedWithGisFavorite && this.hasNameOrLocationChanged && !window.confirm(this.updateText)) {
      return;
    }

    if (!this.errorsExist) {
      this.disabled = true;

      try {
        await this.updateZone({ id: this.zone.id, zone: this.input });
        goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
      } finally {
        this.disabled = false;
      }
    }
  }

  private deleteZoneButton() {
    if (window.confirm(this.deleteText)) {
      this.handleZoneDelete();
    }
  }

  private async handleZoneDelete() {
    this.disabled = true;
    try {
      await this.deleteZone({ id: this.zone.id });
      goBackWithFallback(this.$router, !!this.from.name, this.backLocation);
    } catch (error) {
      this.errorMessage = 'Fehler beim Löschen der Zone. Bitte versuchen sie es später erneut.'
    } finally {
      this.disabled = false;
    }
  }
}
